import { useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'

import { useGraphqlClients } from '../../../app/_providers/GraphqlClientsProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../../brunswick/containers/cart'
import { SeoMeta } from '../../../components/SeoMeta'
import { CREATE_IDME_SHOPIFY_DISCOUNT_CODE } from '../../../lib/graphql/mutations/accountServices/CREATE_IDME_SHOPIFY_DISCOUNT_CODE'
import { MagnoliaPage } from '../MagnoliaApp'

import { Body, StatusText } from './styles'
import { getAuthCodeParam, onIdmeError, onIdmeSuccess } from './utils'

/**
 * ID.me redirect page
 *
 * - users sent here from checkout, `?code=foo` param from id.me
 * - this page uses idme authCode to get discount code from account-services
 * - account-services createDiscountCode mutation is passed back to checkout as discount query param
 * - do not SSR: not cacheable and only clients can authenticate
 * - common error: user reloads page, stale auth code is invalid
 * - NOTE: remember to whitelist the redirect URI in ID.me dashboard, account-services and within Shopify `idme-checkout-template.js` for local development
 */
export const IdmePage: MagnoliaPage<{}> = () => {
  const { authorizedClient: figsAuthedClient } = useGraphqlClients()
  const { idme: strings } = useTranslation()
  const { proceedToCheckout } = CartContainer.useContainer()
  const [discountCode, setDiscountCode] = useState<string | null>(null)
  const [statusText, setStatusText] = useState('')

  const [createIdmeShopifyDiscountCode, { loading }] = useMutation(
    CREATE_IDME_SHOPIFY_DISCOUNT_CODE,
    {
      client: figsAuthedClient,
      onError(error) {
        onIdmeError(error, setStatusText, strings)
      },
      onCompleted(data) {
        onIdmeSuccess(data, setStatusText, setDiscountCode, strings)
      },
    }
  )

  useEffect(() => {
    if (discountCode) proceedToCheckout({ discountCode })
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountCode])

  useEffect(() => {
    const authCode = getAuthCodeParam()

    if (!authCode) {
      setStatusText(strings.missing)
      return
    }

    createIdmeShopifyDiscountCode({
      variables: {
        authCode: authCode,
        source: 'web',
      },
    })
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <SeoMeta title='ID.me' />
      <Body>
        {loading ? (
          <StatusText>{strings.loading}</StatusText>
        ) : (
          <StatusText>{statusText}</StatusText>
        )}
      </Body>
    </>
  )
}

export default IdmePage
